import React from 'react'
import PropTypes from 'prop-types'
import queryString from 'query-string'
import { graphql } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import styled from 'styled-components'
import { rem } from 'polished'
import ReactMarkdown from 'react-markdown'
import unwrapImages from 'remark-unwrap-images'
import { COLOR, FONT_SIZE, FONT_WEIGHT, SPACE, mq } from 'Theme'
import { fullWidthStyle } from 'components/mixins'
import GenericPage from 'components/GenericPage'
import { Box } from 'components/Layout'
import { Text } from 'components/Typography'
import SEO from 'components/SEO'
import ProfileBlock from 'routes/components/ProfileBlock'
import rehypeRaw from 'rehype-raw'
import ReviewCTA from 'components/ReviewCTA'
import PostContainer from './components/PostContainer'
import { CodeBlock, InlineCode } from './components/markdownRenderers'
import Hero from './components/Hero'

export const query = graphql`
  query($slug: String!) {
    sanityBlogPost(slug: { current: { eq: $slug } }) {
      title
      description
      keywords
      markdownInput
      blogImages {
        asset {
          url
          localFile {
            childImageSharp {
              gatsbyImageData(layout: FULL_WIDTH)
              original {
                width
              }
            }
          }
        }
        caption
      }
      author {
        name
        image {
          asset {
            localFile {
              childImageSharp {
                gatsbyImageData(layout: CONSTRAINED)
              }
            }
          }
        }
        socialNetworks {
          linkedin
          twitter
        }
      }
      ogImage {
        asset {
          url
        }
      }
    }
  }
`

export const ArticleStyle = styled('div')`
  display: contents;

  font-family: Merriweather, serif;
  font-size: ${FONT_SIZE.XL};
  line-height: 2.1;

  color: ${COLOR.BLACK};

  ${mq.from.M`
    font-size: ${FONT_SIZE.L};
  `}

  blockquote {
    padding: ${SPACE.L} 0;
    margin: ${SPACE.XL} 0;

    font-family: Poppins, sans-serif;
    font-size: ${FONT_SIZE.XXXL};
    line-height: 1.625;
    text-align: center;

    border: solid ${COLOR.BLACK};
    border-width: 1px 0;

    ${mq.from.M`
      padding: ${SPACE.XL} 0;

      font-size: ${FONT_SIZE.XXXXL};
    `}
  }

  a {
    color: ${COLOR.PRIMARY_RED};

    &:not(:hover) {
      text-decoration: none;
    }
  }

  > * {
    margin: ${rem(48)} auto;
  }
`

const StyledImg = styled(GatsbyImage)`
  ${({ maxWidth }) =>
    maxWidth &&
    `
      max-width: ${maxWidth}px;
      margin-right: auto;
      margin-left: auto;
    `}
`

export const Title = styled('h1')`
  font-family: Poppins, sans-serif;
  font-size: ${FONT_SIZE.XXXXXL};
  font-weight: ${FONT_WEIGHT.NORMAL};
  line-height: 1.5;
  letter-spacing: -0.02em;
  text-align: center;

  ${mq.from.M`
    font-size: ${FONT_SIZE.XXXXXXL};
  `}
`

export const Description = styled('p')`
  font-family: Poppins, sans-serif;
  font-size: ${FONT_SIZE.XXL};
  line-height: 1.5;
  text-align: center;

  ${mq.from.M`
    font-size: ${FONT_SIZE.XL};
    line-height: 2;
  `}
`

const StyledWrapper = styled('div')`
  display: flex;
  justify-content: center;
  margin-top: ${SPACE.XL};
  border-top: 1px solid ${COLOR.WHITE_75};
  border-bottom: 1px solid ${COLOR.WHITE_75};
`

const StyledPreFooter = styled('div')`
  padding: ${SPACE.XL} 0;
`

const PreFooterTitle = styled('div')`
  font-size: ${FONT_SIZE.XXXXXL};
  text-align: center;
  padding-bottom: ${SPACE.XL};
  line-height: 0.9;
`

const Article = ({
  data: {
    sanityBlogPost: {
      title,
      description,
      keywords,
      markdownInput,
      blogImages,
      author: { name, image, socialNetworks },
      ogImage,
    },
  },
}) => {
  const { asset: { url: ogImageUrl } = {} } = ogImage || {}
  const {
    asset: {
      localFile: { childImageSharp: headingImageSharp },
    },
    caption: headingImageCaption,
  } = blogImages[0] ?? {}

  return (
    <GenericPage>
      <SEO title={title} keywords={keywords} image={ogImageUrl} />
      <Box css={fullWidthStyle} backgroundColor={COLOR.WHITE}>
        <Hero>
          <Title>{title}</Title>
          <Description>{description}</Description>
          <ProfileBlock
            name={name}
            avatar={
              <GatsbyImage
                image={image.asset.localFile.childImageSharp.gatsbyImageData}
              />
            }
            socialNetworks={socialNetworks}
          />
          {headingImageSharp && (
            <StyledImg image={headingImageSharp.gatsbyImageData} />
          )}
          {headingImageCaption && (
            <Text
              as="p"
              textAlign="center"
              color={COLOR.BLACK}
              py={SPACE.M}
              opacity={0.5}
            >
              {headingImageCaption}
            </Text>
          )}
        </Hero>
        <PostContainer>
          <ArticleStyle>
            <ReactMarkdown
              // eslint-disable-next-line react/no-children-prop
              children={markdownInput}
              linkTarget="_blank"
              plugins={[unwrapImages]}
              rehypePlugins={[rehypeRaw]}
              components={{
                code: CodeBlock,
                pre: InlineCode,
                // eslint-disable-next-line react/prop-types
                img: ({ src }) => {
                  const { url: srcWithoutQueryString } = queryString.parseUrl(
                    src
                  )
                  const {
                    asset: {
                      localFile: { childImageSharp },
                    },
                  } = blogImages.find(
                    ({ asset }) => asset.url === srcWithoutQueryString
                  )
                  return (
                    <StyledImg
                      image={childImageSharp.gatsbyImageData}
                      maxWidth={
                        childImageSharp.original.width <
                        Number.parseFloat('720px')
                          ? childImageSharp.original.width
                          : undefined
                      }
                    />
                  )
                },
              }}
            />
          </ArticleStyle>
        </PostContainer>
      </Box>
      <StyledWrapper>
        <StyledPreFooter>
          <PreFooterTitle>Prihlás sa na kurz Sudo Academy</PreFooterTitle>
          <ReviewCTA buttonTitle="Prihlás sa na kurz" linkTo="/" />
        </StyledPreFooter>
      </StyledWrapper>
    </GenericPage>
  )
}

Article.propTypes = {
  data: PropTypes.shape({
    sanityBlogPost: PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      keywords: PropTypes.arrayOf(PropTypes.string).isRequired,
      markdownInput: PropTypes.string.isRequired,
      blogImages: PropTypes.arrayOf(
        PropTypes.shape({
          asset: PropTypes.shape({
            url: PropTypes.string.isRequired,
            localFile: PropTypes.shape({
              childImageSharp: PropTypes.shape({
                fluid: PropTypes.object.isRequired,
                original: PropTypes.shape({
                  width: PropTypes.number.isRequired,
                }).isRequired,
              }).isRequired,
            }).isRequired,
          }).isRequired,
          caption: PropTypes.string,
        }).isRequired
      ).isRequired,
      author: PropTypes.shape({
        name: PropTypes.string.isRequired,
        image: PropTypes.shape({
          asset: PropTypes.shape({
            fluid: PropTypes.object.isRequired,
          }).isRequired,
        }).isRequired,
        socialNetworks: PropTypes.shape({
          twitter: PropTypes.string,
          linkedin: PropTypes.string,
        }),
      }).isRequired,
      ogImage: PropTypes.shape({
        asset: PropTypes.shape({
          url: PropTypes.string,
        }),
      }),
    }).isRequired,
  }).isRequired,
}

export default Article
